"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { Button, Stack } from "@leabankab/component-library";
import { StepLoaderBody } from "@/components/loading";
import { useFlowTaskPolling } from "@/features/waiting/use-flow-task-polling";
import { clearSignDispatch } from "../sign-dispatch-actions";

interface Props {
	flowId: string;
	/** The open signing task, which this browser has already been sent to sign. */
	taskId: string;
}

/**
 * Shown when the customer returns from the signing provider before FLOW has
 * recorded the signature. Polls until FLOW hands out different work, and offers
 * a retry once the wait stops looking routine - a customer who abandoned the
 * provider instead of signing needs a way back to it.
 */
export const SignPendingScreen = ({ flowId, taskId }: Props) => {
	const t = useTranslations("common");
	const router = useRouter();
	const phase = useFlowTaskPolling({ flowId, ignoreTaskId: taskId });
	const [retrying, setRetrying] = useState(false);

	const retry = async () => {
		setRetrying(true);
		await clearSignDispatch();
		// Back through the resolver rather than straight to `sign`, which is the
		// route already rendering here.
		router.replace(`/${flowId}`);
	};

	const canRetry = phase === "slow" || phase === "verySlow";

	return (
		<Stack gap={2}>
			<StepLoaderBody as="h2" phase={phase} />
			{canRetry && (
				<Button
					variant="outline"
					onClick={() => void retry()}
					disabled={retrying}
					aria-busy={retrying}
					className="w-full"
				>
					{t("tryAgain")}
				</Button>
			)}
		</Stack>
	);
};
